<template>
  <div>
    <v-line :config="borderConfig" />

    <v-line :config="innerBackgroundConfig" />

    <v-circle v-for="(config, i) of cornersConfig" :key="i" :config="config" />

    <v-group v-if="closed && longer_size_length">
      <div v-for="(lineConfig, i) in gridLines" :key="`line ${i}`">
        <v-line :config="{ ...lineConfig, strokeWidth: 1 / scale }" />
      </div>
      <div
        v-for="(sideConfig, i) in getInsideSideConfigs"
        :key="`inside line ${i}`"
      >
        <v-line :config="sideConfig" />
      </div>
    </v-group>

    <v-group v-if="closed && longer_size_length">
      <div v-for="(pointConfig, i) in pointConfigs" :key="`point ${i}`">
        <v-circle
          :config="pointConfig"
          @click="tab === 1 && activateDeactivatePointClicked(pointConfig.id)"
        />
      </div>
      <div
        v-for="(lineMarksConfigs, i) in lineMarksConfig"
        :key="`points ${i}`"
      >
        <v-line
          :config="{
            points: [
              lineMarksConfigs.x,
              lineMarksConfigs.y,
              lineMarksConfigs.lineX,
              lineMarksConfigs.lineY,
            ],
            opacity: 1,
            stroke: 'white',
            strokeWidth: 1 / scale,
          }"
        ></v-line>
        <v-circle :config="lineMarksConfigs" />
        <v-text
          :config="{
            x:
              lineMarksConfigs.id.length > 1 || lineMarksConfigs.id > 9
                ? lineMarksConfigs.x - 8 / scale
                : lineMarksConfigs.x - 5 / scale,
            y: lineMarksConfigs.y - 5.1 / scale,
            align: 'left',
            text: lineMarksConfigs.id,
            fontSize: 12 / scale,
            fontFamily: 'Roboto',
            fill: 'black',
          }"
        />
      </div>

      <div>
        <v-rect :config="outerRectangleConfig" />
        <v-rect :config="widthSizeTextBlob" />
        <v-text :config="widthText" />

        <v-rect :config="heightSizeTextBlob" />
        <v-text :config="heightText" />
      </div>
    </v-group>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { eventBus } from "@/components/EventBus";
import { chunk, last } from "lodash";
import { calculateLineConfigs } from "./roomGridHelpers";
import { getIntersection } from "./calculations";
import {
  calculateRectangleProperties,
  getParallelWalls,
  calcPolygonArea,
} from "./roomPropertiesHelpers";
import { alphabet } from "@/utils/helpers";

const areIntersecting = (x1, y1, x2, y2, x3, y3, x4, y4) => {
  let det, gamma, lambda;
  det = (x2 - x1) * (y4 - y3) - (x4 - x3) * (y2 - y1);
  if (det === 0) {
    return false;
  }

  lambda = ((y4 - y3) * (x4 - x1) + (x3 - x4) * (y4 - y1)) / det;
  gamma = ((y1 - y2) * (x4 - x1) + (x2 - x1) * (y4 - y1)) / det;
  return 0 < lambda && lambda < 1 && 0 < gamma && gamma < 1;
};

const isRoomClosed = (corners, x, y) =>
  corners.length > 3 &&
  Math.abs(x - corners[0]) < 10 &&
  Math.abs(y - corners[1]) < 10;

export default {
  name: "room-drawing-container",
  props: ["scale"],
  data: () => ({
    hoverPosition: [],
    lineStrokeColor: undefined,
    hasIntersection: false,
    lineMarksCoordinates: [],
    gridLines: [],
  }),
  computed: {
    ...mapState("roomStore", [
      "addingRoom",
      "isDrawing",
      "updatingRoom",
      "corners",
      "measurement_points",
      "closed",
      "longer_size_length",
      "distance_between_points_x",
      "distance_between_points_y",
      "distance_from_wall",
      "tab",
      "selected_measurement_point_id",
    ]),

    widthSizeTextBlob() {
      const { xMax, yMax, width } = this.rectangleProperties;

      return {
        x: xMax - width / 2 - 22.5 / this.scale,
        y: yMax + 1 / this.scale,
        width: 64 / this.scale,
        height: 28 / this.scale,
        fill: "#ff713d",
        cornerRadius: 100,
        listening: false,
      };
    },
    widthText() {
      const { xMax, yMax, width, height, shorterSideInMeters } =
        this.rectangleProperties;

      const text =
        width > height
          ? this.longer_size_length
          : shorterSideInMeters.toFixed(1);

      return {
        x: xMax - width / 2 - 22.5 / this.scale + 16 / this.scale,
        y: yMax + 1 / this.scale + 8 / this.scale,
        text: `${text} m`,
        fontSize: 12 / this.scale,
        fontFamily: "Roboto",
        fill: "black",
        listening: false,
      };
    },
    heightSizeTextBlob() {
      const { xMax, yMax, height } = this.rectangleProperties;

      return {
        x: xMax + 1 / this.scale,
        y: yMax - height / 2 - 22.5 / this.scale,
        width: 55 / this.scale,
        height: 28 / this.scale,
        fill: "#ff713d",
        cornerRadius: 100,
        listening: false,
      };
    },
    heightText() {
      const { xMax, yMax, height, width, shorterSideInMeters } =
        this.rectangleProperties;

      const text =
        height > width
          ? this.longer_size_length
          : shorterSideInMeters.toFixed(1);

      return {
        x: xMax + 1 / this.scale + 14 / this.scale,
        y: yMax - height / 2 - 14 / this.scale,
        text: `${text} m`,
        fontSize: 12 / this.scale,
        fontFamily: "Roboto",
        fill: "black",
        listening: false,
      };
    },
    outerRectangleConfig() {
      const { xMin, yMin, height, width } = this.rectangleProperties;

      return {
        x: xMin,
        y: yMin,
        width: width,
        height: height,
        strokeWidth: 3 / this.scale,
        fillEnabled: false,
        stroke: "#ff713d",
        listening: false,
      };
    },
    rectangleProperties() {
      return calculateRectangleProperties(
        this.corners,
        this.longer_size_length
      );
    },
    getInsideRoomCorners() {
      if (!this.corners?.length > 0 || !this.longer_size_length) {
        return null;
      }

      let corners;

      const { height, width } = this.rectangleProperties;

      const pixelsInMeter = Math.max(height, width) / this.longer_size_length;

      const parallelWalls1 = getParallelWalls(
        this.corners,
        pixelsInMeter,
        true,
        this.distance_from_wall
      );
      const parallelWalls2 = getParallelWalls(
        this.corners,
        pixelsInMeter,
        false,
        this.distance_from_wall
      );
      const pairs1 = [];
      const pairs2 = [];

      for (let i = 0; i < parallelWalls1.length; i++) {
        pairs1.push([
          parallelWalls1[i],
          parallelWalls1[i === parallelWalls1.length - 1 ? 0 : i + 1],
        ]);
      }
      for (let i = 0; i < parallelWalls2.length; i++) {
        pairs2.push([
          parallelWalls2[i],
          parallelWalls2[i === parallelWalls2.length - 1 ? 0 : i + 1],
        ]);
      }

      const corners1 = pairs1.map((pair) =>
        getIntersection(...pair[0], ...pair[1], false)
      );
      const corners2 = pairs2.map((pair) =>
        getIntersection(...pair[0], ...pair[1], false)
      );

      // compare and decide which "parallel polygon" is inside the original polygon and which one is outside
      const area1 = calcPolygonArea(corners1);
      const area2 = calcPolygonArea(corners2);

      if (area1 > area2) {
        corners = corners2;
      } else {
        corners = corners1;
      }

      const cornersInArray = [];

      corners.forEach((corner) => {
        cornersInArray.push(corner.x);
        cornersInArray.push(corner.y);
      });
      return cornersInArray;
    },
    getInsideSideConfigs() {
      if (!this.corners?.length > 0 || !this.longer_size_length) {
        return null;
      }

      const corners = this.getInsideRoomCorners;
      const insideRoomSides = [];

      for (let i = 0; i < corners.length; i = i + 2) {
        if (i === corners.length - 2) {
          insideRoomSides.push([
            ...corners.slice(i, i + 2),
            ...corners.slice(0, 2),
          ]);
        } else {
          insideRoomSides.push([...corners.slice(i, i + 4)]);
        }
      }

      return insideRoomSides.map((side) => ({
        points: side,
        stroke: "white",
        strokeWidth: 2 / this.scale,
        opacity: 1,
        listening: false,
      }));
    },
    borderConfig() {
      return {
        points: [...this.corners, ...this.hoverPosition],
        stroke: this.lineStrokeColor,
        strokeWidth: 3 / this.scale,
        closed: this.closed,
        opacity: 1,
        listening: false,
      };
    },
    pointConfigs() {
      return this.measurement_points.map((point) => {
        return {
          x: point.x,
          y: point.y,
          id: point.location,
          radius:
            point.measurement_point_id === this.selected_measurement_point_id
              ? 8 / this.scale
              : 6 / this.scale,
          fill:
            point.measurement_point_id === this.selected_measurement_point_id
              ? "#ff713d"
              : point.is_active
              ? "#3c74ff"
              : this.$vuetify.theme.themes.dark.gray,
          listening: this.tab === 1,
        };
      });
    },
    lineMarksConfig() {
      return this.lineMarksCoordinates.map((point) => {
        return {
          x: Number.isInteger(point.id)
            ? Number(point.marksX - 30 / this.scale)
            : Number(point.marksX),
          y: !Number.isInteger(point.id)
            ? Number(point.marksY - 30 / this.scale)
            : Number(point.marksY),
          lineX: point.gridPointX,
          lineY: point.gridPointY,
          id: point.id,
          radius: 10 / this.scale,
          fill: "white",
          stroke: "#aaa",
          strokeWidth: 1 / this.scale,
          listening: false,
        };
      });
    },
    innerBackgroundConfig() {
      return {
        points: [...this.corners, ...this.hoverPosition],
        stroke: "#856d63",
        strokeWidth: 3 / this.scale,
        closed: true,
        fill: "#856d63",
        opacity: this.closed ? 0.5 : 0.1,
        listening: false,
      };
    },
    cornersConfig() {
      if (!this.addingRoom && !this.updatingRoom) {
        return [];
      }

      const allCurrentDots = this.isDrawing
        ? [...this.corners, ...this.hoverPosition]
        : this.corners;

      const separateDotCoordinates = chunk(allCurrentDots, 2);

      return separateDotCoordinates.map((coordinates) => ({
        x: coordinates[0],
        y: coordinates[1],
        radius: 6 / this.scale,
        fill: this.lineStrokeColor,
        listening: false,
      }));
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "setIsRoomClosed",
      "setRoomCorner",
      "resetRoomState",
      "resetRoomCorners",
      "setIsDrawing",
      "setMeasurementPointsByCalculation",
      "setFreezeDistanceBetweenPoints",
      "setMaxDistanceBetweenPoints",
      "activateDeactivatePoint",
    ]),
    async isLastLineIntersecting() {
      const allCurrentCoordinates = [...this.corners, ...this.hoverPosition];
      const separateDotCoordinates = chunk(allCurrentCoordinates, 2);

      if (separateDotCoordinates.length < 4) {
        this.hasIntersection = false;
        return;
      }

      const reducer = (
        accumulator,
        currentValue,
        index,
        separateCoordinates
      ) => {
        if (index !== separateCoordinates?.length - 1) {
          accumulator.push([currentValue, separateCoordinates[index + 1]]);
        }

        return accumulator;
      };

      let allLines = separateDotCoordinates.reduce(reducer, []);

      const lastLine = last(allLines);

      const intersections = await allLines.map((line, index) => {
        if (index === allLines.length - 1) {
          // to ignore last line aka hover position line
          return false;
        }

        return areIntersecting(
          line[0][0],
          line[0][1],
          line[1][0],
          line[1][1],
          lastLine[0][0],
          lastLine[0][1],
          lastLine[1][0],
          lastLine[1][1]
        );
      });

      this.hasIntersection = intersections.some(
        (intersection) => !!intersection
      );
    },
    addRoomCorner(fixed, stage) {
      if (
        this.closed ||
        (!this.addingRoom && !this.updatingRoom) ||
        !this.isDrawing
      ) {
        return;
      }

      const pos = stage.getNode().getPointerPosition();
      const absPos = stage.getNode().getAbsolutePosition();
      const x = (pos.x - absPos.x) / this.scale;
      const y = (pos.y - absPos.y) / this.scale;

      if (!fixed) {
        const theme = this.$vuetify.theme.themes.dark;
        this.hoverPosition = [x, y];
        this.isLastLineIntersecting();
        this.lineStrokeColor = isRoomClosed(this.corners, x, y)
          ? "#3c74ff"
          : this.hasIntersection
          ? theme.error
          : "#ff713d";
        return;
      }

      if (this.hasIntersection) {
        return;
      }
      if (!isRoomClosed(this.corners, x, y)) {
        this.setRoomCorner([x, y]);
      }

      isRoomClosed(this.corners, x, y) && this.setIsRoomClosed(true);
    },
    addlineMarks(points) {
      this.lineMarksCoordinates.push(points);
    },
    clearMarks() {
      this.lineMarksCoordinates = [];
    },
    activateDeactivatePointClicked(id) {
      this.activateDeactivatePoint(id);
    },
    getGridLines() {
      if (!this.corners?.length > 0 || !this.longer_size_length) {
        this.gridLines = null;
        return;
      }
      const { horizontalLineConfigs, verticalLineConfigs } =
        calculateLineConfigs(this.corners, this.longer_size_length);
      this.setFreezeDistanceBetweenPoints(false);

      this.clearMarks();
      for (let i = 0; i < horizontalLineConfigs.length; i++) {
        const horizontalX = horizontalLineConfigs[i]["points"][0];
        const horizontalY = horizontalLineConfigs[i]["points"][1];
        const cord = {
          marksX: horizontalX,
          marksY: horizontalY,
          gridPointX: Number(horizontalX),
          gridPointY: Number(horizontalY),
          id: i + 1,
        };
        this.addlineMarks(cord);
      }

      for (let i = 0; i < verticalLineConfigs.length; i++) {
        const verticalX = verticalLineConfigs[i]["points"][0];
        const verticalY = verticalLineConfigs[i]["points"][1];
        const cord = {
          marksX: verticalX,
          marksY: verticalY,
          gridPointX: Number(verticalX),
          gridPointY: Number(verticalY),
          id: alphabet(i + 1),
        };
        this.addlineMarks(cord);
      }

      this.gridLines = [...horizontalLineConfigs, ...verticalLineConfigs];
    },
  },
  created() {
    if (!this.closed) {
      this.lineStrokeColor = "#ff713d";

      eventBus.$on("addRoomCorner", (fixed, stage) =>
        this.addRoomCorner(fixed, stage)
      );
    } else {
      this.lineStrokeColor = "#3c74ff";
    }
    eventBus.$on("centerRoomWithStage", (stage, savePlan) => {
      const node = stage.getNode();
      var center = {
        x: node.width() / 2,
        y: node.height() / 2,
      };
      const { xMin, yMin, xMax, yMax } = calculateRectangleProperties(
        this.corners
      );
      const room = {
        x: (xMax + xMin) / 2,
        y: (yMax + yMin) / 2,
      };
      var newPos = {
        x: center.x - room.x * this.scale,
        y: center.y - room.y * this.scale,
      };
      if (newPos.x && newPos.y) {
        node.position(newPos);
        if (savePlan) {
          savePlan(
            node.toDataURL({
              pixelRatio: 2,
            })
          );
        }
      } else {
        if (savePlan) {
          savePlan();
        }
      }
    });
  },
  destroyed() {
    eventBus.$off("centerRoomWithStage");
  },
  watch: {
    isDrawing(value) {
      if (!value && !this.closed) {
        this.resetRoomCorners();
      }
    },

    addingRoom(value) {
      if (this.corners?.length === 0 && this.closed) {
        this.setIsRoomClosed(false);
      }

      if (!value) {
        this.resetRoomState();
        this.setIsDrawing(false);
      }
    },
    closed(value) {
      value && this.setIsDrawing(false);
      value &&
        this.setMeasurementPointsByCalculation(this.getInsideRoomCorners);
      this.getGridLines();
      if (value) {
        this.hoverPosition = [];
      }
      this.lineStrokeColor = value ? "#3c74ff" : "#ff713d";
    },
    longer_size_length() {
      this.closed &&
        this.setMeasurementPointsByCalculation(this.getInsideRoomCorners);
      this.closed && this.getGridLines();
    },
    distance_between_points_x() {
      this.closed &&
        this.setMeasurementPointsByCalculation(this.getInsideRoomCorners);
      this.closed && this.getGridLines();
    },
    distance_between_points_y() {
      this.closed &&
        this.setMeasurementPointsByCalculation(this.getInsideRoomCorners);
      this.closed && this.getGridLines();
    },
    distance_from_wall() {
      this.closed &&
        this.setMeasurementPointsByCalculation(this.getInsideRoomCorners);
      this.closed && this.getGridLines();
    },
  },
};
</script>
